import toast from 'react-hot-toast';
import { LogonStatus, UserType } from '../application-form/Variables';
import { ApiResponse, getData } from './Apis';
import { MfaTypeModel } from './ApisAccountSecurity';

export type LoginResponseModel = {
    Status: LogonStatus,
    MFAPrompt: boolean,
    MFALocked: boolean,
    MFAHelperMessage: string,
    MFALockedMin: string,
    MFATypes: MfaTypeModel[],
}

export type LoginMFAResponseModel = {
    LoginSuccess: boolean,
    MFALocked: boolean,
    MFAHelperMessage: string,
    MFALockedMin: string,
}

export type ChangeMFAResponseModel = {
    MFALocked: boolean,
    MFAHelperMessage: string,
    MFALockedMin: string,
}

export type UserResponseModel = {
    FullName: string,
    UserType: UserType,
    EmailAuthenticated: boolean,
    PersonalDetailsFilledIn: boolean,
    PrivacyPolicyUpdateRequired: boolean,
}

export type PasswordResetRequestModel = {
    Requirements: string,
    ValidID: boolean
}

export type PasswordChangeResponseModel = {
    Error: string,
    Success: boolean
}


export async function API_RequestUsername(emailAddress: string) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    return await fetch(`${window.location.origin}/webapi/candidate/forgotpassword/Username?emailAddress=${emailAddress}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        }
    });
}

export async function API_RequestPasswordReset(username: string) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    return await fetch(`${window.location.origin}/webapi/candidate/forgotpassword/Password?username=${username}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        }
    });
}

export async function API_CheckPasswordRequestID(id: string) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    var response = await fetch(`${window.location.origin}/webapi/candidate/forgotpassword/CheckID?id=${id}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        }
    });
    if (response.ok) {
        return await response.json() as ApiResponse<PasswordResetRequestModel>;
    }
}

export async function API_Login(username: string, password: string) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    const response = await fetch(`${window.location.origin}/webapi/candidate/Login`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            'Content-Type': 'application/json',
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        },
        body: JSON.stringify({ Username: username, Password: password }),
    });

    if (response.ok) {
        return await response.json() as ApiResponse<LoginResponseModel>;
    }

    toast.error("Error");
    throw new Error('Unexpected error');
}

export async function API_LoginMFA(username: string, code: string, mfaType: string) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    try {
        const response = await fetch(`${window.location.origin}/webapi/candidate/LoginMFA`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                'Content-Type': 'application/json',
                'Accepts': 'application/json',
                '__RequestVerificationToken': token,
            },
            body: JSON.stringify({ Code: code, Username: username, MfaType: mfaType }),
        });

        if (response.ok) {
            return await response.json() as ApiResponse<LoginMFAResponseModel>;
        }

        if (response.status == 403) {
            return { Error: "Login Expired" } as ApiResponse<LoginMFAResponseModel>;
        }
    } catch {
        return { Error: "Login Expired" } as ApiResponse<LoginMFAResponseModel>;
    }

    toast.error("Error");
    throw new Error('Unexpected error');
}

export async function API_ChangeMFAType(username: string, mfaType: string) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    try {
        const response = await fetch(`${window.location.origin}/webapi/candidate/ChangeMFAType`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                'Content-Type': 'application/json',
                'Accepts': 'application/json',
                '__RequestVerificationToken': token,
            },
            body: JSON.stringify({ username, mfaType }),
        });

        if (response.ok) {
            return await response.json() as ApiResponse<ChangeMFAResponseModel>;
        }

        if (response.status == 403) {
            return { Error: "Login Expired" } as ApiResponse<ChangeMFAResponseModel>;
        }
    } catch {
        return { Error: "Login Expired" } as ApiResponse<ChangeMFAResponseModel>;
    }

    toast.error("Error");
    throw new Error('Unexpected error');
}

export async function API_VerifyLogin(): Promise<UserResponseModel> {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';

    try {
        const response = await fetch(`${window.location.origin}/webapi/candidate/VerifyLogin`, {
            method: "GET",
            credentials: "same-origin",
            headers: {
                'Accepts': 'application/json',
                '__RequestVerificationToken': token,
            }
        });
        if (response.status == 403) {
            return { UserType: UserType.Anonymous } as UserResponseModel;
        }
        else if (response.ok) {
            return await response.json() as UserResponseModel;
        }
    } catch {
        return { UserType: UserType.Anonymous } as UserResponseModel;
    }
}

export async function API_LogOut(): Promise<ApiResponse<{}>> {
    return await getData<ApiResponse<{}>>("webapi/candidate/Logout");
}

export async function API_ChangePassword(password: string, passwordConfirmation: string, id: string): Promise<PasswordChangeResponseModel> {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    var response = await fetch(`${window.location.origin}/webapi/candidate/forgotpassword/ChangePassword`, {
        method: "POST",
        credentials: "same-origin",
        headers: { __RequestVerificationToken: token, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ PasswordConfirmation: passwordConfirmation, Password : password, Id: id }),
    });
    if (response.ok) {
        return await response.json() as PasswordChangeResponseModel;
    }
}